import { put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { get } from 'lodash';
import dayjs from 'dayjs';
import {
    cleanOldTickets,
    groupedLineClicked,
    groupedTicketClicked,
    lineClicked,
    lineLongPressed,
    popOverClicked,
    PopOverType,
    ticketClicked,
    undisplayGroupedTickets,
    undisplayTicket,
} from './user.actions';
import type { FireStoreUpdates } from './types';
import {
    getIsAssembleEnabled,
    getIsDoubleValidationEnabled,
    getIsProductFollowUpEnabled,
    getRecallTimeBoundaries,
} from '../configuration/selectors';
import { assertUnreachable } from '../../utils/typescript';
import { FireStoreUpdatesMap, updateBatchTickets, updateTicket } from './firestore.actions';
import { getRestaurantId } from '../user/selectors';
import { getMapPath, hasChildren } from '../../services/tickets/getMapPath';
import { Routes } from '../../routes.types';
import { getSelector, TicketSelector } from './selectorLibs/getSelectors';
import { getAllTickets, getBoardTickets } from './selector';
import { KdsTicket, TicketStatus } from './types/ticket';
import { KdsLine, KdsLinesMap } from './types/kds-line';
import { LineStatus } from './types/kds-line/status';
import { getLineNextStatus } from './middlewares/getLineNextStatus';
import { getLinePreviousStatus } from './middlewares/getLinePreviousStatus';
import { getTicketStatusUpdate } from './middlewares/getTicketStatusUpdate';

function getAllKdsLinesUpdates({
    kdsLines,
    isDoubleValidationEnabled,
    isProductFollowUpEnabled,
    parentPath,
    route,
    isAssembleEnabled,
}: {
    kdsLines: KdsLinesMap;
    isDoubleValidationEnabled: boolean;
    isProductFollowUpEnabled: boolean;
    isAssembleEnabled: boolean;
    parentPath?: string;
    route: Routes;
}): FireStoreUpdates {
    return Object.values(kdsLines).reduce<FireStoreUpdates>((updates, kdsLine) => {
        const { uuid: kdsLineUuid } = kdsLine;
        if (hasChildren(kdsLine)) {
            return {
                ...updates,
                ...getAllKdsLinesUpdates({
                    kdsLines: kdsLine.kdsLines,
                    isDoubleValidationEnabled,
                    isProductFollowUpEnabled,
                    parentPath: getMapPath({ kdsLineUuid, parentPath }),
                    isAssembleEnabled,
                    route,
                }),
            };
        }

        return {
            ...updates,
            [getMapPath({
                kdsLineUuid,
                parentPath,
                attribute: 'status',
            })]: getLineNextStatus({
                kdsLine,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                isAssembleEnabled,
                isAChildren: true,
                route,
            }),
        };
    }, {});
}

function getKdsLineUpdates(
    linePath: string,
    line: KdsLine,
    route: Routes,
    isDoubleValidationEnabled: boolean,
    isProductFollowUpEnabled: boolean,
    isAssembleEnabled: boolean,
) {
    let updates: FireStoreUpdates = {};

    if (hasChildren(line)) {
        updates = {
            ...getAllKdsLinesUpdates({
                kdsLines: line.kdsLines,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                parentPath: linePath,
                isAssembleEnabled,
                route,
            }),
        };
    } else {
        updates = {
            [`${linePath}.status`]: getLineNextStatus({
                kdsLine: line,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                isAssembleEnabled,
                isAChildren: false,
                route,
            }),
        };
    }

    return updates;
}

const ticketIsPreparingSinceLessThanTiming =
    (timing: number) =>
    (ticket: KdsTicket): boolean =>
        dayjs().isAfter(dayjs(ticket.prepareAt).add(timing, 'minutes'));

function* getContext(
    orderId: string,
    route: Routes,
): Generator<
    unknown,
    {
        ticket: KdsTicket | undefined;
        restaurantId: number;
        isDoubleValidationEnabled: boolean;
        isAssembleEnabled: boolean;
        isProductFollowUpEnabled: boolean;
    }
> {
    const contextTickets = (yield select(getSelector(route))) as ReturnType<TicketSelector>;
    const ticket = contextTickets.find((contextTicket) => contextTicket.orderId === orderId);
    const restaurantId = (yield select(getRestaurantId)) as ReturnType<typeof getRestaurantId>;
    if (restaurantId === undefined) {
        throw new Error('RestaurantId is undefined. The configuration must have been fetched');
    }
    const isDoubleValidationEnabled = (yield select(getIsDoubleValidationEnabled)) as ReturnType<
        typeof getIsDoubleValidationEnabled
    >;
    const isAssembleEnabled = (yield select(getIsAssembleEnabled)) as ReturnType<typeof getIsAssembleEnabled>;
    const isProductFollowUpEnabled = (yield select(getIsProductFollowUpEnabled)) as ReturnType<
        typeof getIsProductFollowUpEnabled
    >;

    return { ticket, restaurantId, isDoubleValidationEnabled, isAssembleEnabled, isProductFollowUpEnabled };
}

function* getUnfilteredTicket(orderId: string): Generator<unknown, KdsTicket | undefined> {
    const allTickets = (yield select(getAllTickets)) as ReturnType<typeof getAllTickets>;
    return allTickets.find((ticket) => ticket.orderId === orderId);
}

const isChildLine = (linePath: string): boolean => {
    const pathParts = linePath.split('.');
    const kdsLinesCount = pathParts.filter((part) => part === 'kdsLines').length;
    return kdsLinesCount > 1;
};

const getParentLinePath = (childLinePath: string): string | null => {
    if (!isChildLine(childLinePath)) {
        return null;
    }
    const pathParts = childLinePath.split('.');
    return pathParts.slice(0, -2).join('.');
};

const getAllChildrenSameStatus = (parentLine: KdsLine): LineStatus | null => {
    const children = Object.values(parentLine.kdsLines);
    if (children.length === 0) {
        return null;
    }

    const firstChildStatus = children[0].status;
    const allSameStatus = children.every((child) => child.status === firstChildStatus);

    return allSameStatus ? firstChildStatus : null;
};

function getParentStatusUpdateIfNeeded(
    linePath: string,
    updatedChildLine: KdsLine,
    nextStatus: LineStatus,
    unfilteredTicket: KdsTicket,
): FireStoreUpdates {
    if (!isChildLine(linePath)) {
        return {};
    }

    const parentLinePath = getParentLinePath(linePath);
    if (!parentLinePath) {
        return {};
    }

    const parentLine = get(unfilteredTicket, parentLinePath) as KdsLine;

    const simulatedParent: KdsLine = {
        ...parentLine,
        kdsLines: {
            ...parentLine.kdsLines,
            [updatedChildLine.uuid]: {
                ...updatedChildLine,
                status: nextStatus,
            },
        },
    };

    const commonStatus = getAllChildrenSameStatus(simulatedParent);

    if (commonStatus !== null) {
        const parentUpdate = {
            [`${parentLinePath}.status`]: commonStatus,
        };

        const ancestorUpdates = getParentStatusUpdateIfNeeded(
            parentLinePath,
            { ...simulatedParent, status: commonStatus },
            commonStatus,
            unfilteredTicket,
        );

        return {
            ...parentUpdate,
            ...ancestorUpdates,
        };
    }

    return {};
}

// eslint-disable-next-line complexity
function* handleGroupedLineClicked({
    payload: { clickedLinePath, lines, route },
}: ActionType<typeof groupedLineClicked>): Generator {
    const updateLinesMap: FireStoreUpdatesMap = {};
    const clickedLineIndex = lines.findIndex((line) => line.linePath === clickedLinePath);

    if (clickedLineIndex < 0) {
        return;
    }
    const [clickedLineEvent] = lines.splice(clickedLineIndex, 1);
    const { orderId: clickedOrderId } = clickedLineEvent;
    const {
        ticket: clickedTicket,
        restaurantId: clickedRestaurantId,
        isDoubleValidationEnabled,
        isAssembleEnabled,
        isProductFollowUpEnabled,
    } = yield* getContext(clickedOrderId, route);
    if (clickedTicket == null || clickedTicket.status === TicketStatus.Cancelled) {
        return;
    }

    const unfilteredClickedTicket = (yield* getUnfilteredTicket(clickedOrderId)) as KdsTicket | undefined;
    if (unfilteredClickedTicket === undefined) {
        return;
    }

    const clickedLine = get(clickedTicket, clickedLinePath) as KdsLine;
    const unfilteredClickedLine = get(unfilteredClickedTicket, clickedLinePath) as KdsLine;
    const clickedLineUpdates = getKdsLineUpdates(
        clickedLinePath,
        unfilteredClickedLine,
        route,
        isDoubleValidationEnabled,
        isProductFollowUpEnabled,
        isAssembleEnabled,
    );

    const clickedNextStatus = getLineNextStatus({
        kdsLine: clickedLine,
        isDoubleValidationEnabled,
        isProductFollowUpEnabled,
        isAssembleEnabled,
        isAChildren: false,
        route,
    });

    const clickedParentUpdates = getParentStatusUpdateIfNeeded(
        clickedLinePath,
        unfilteredClickedLine,
        clickedNextStatus,
        unfilteredClickedTicket,
    );

    if (updateLinesMap[clickedOrderId] == null) {
        updateLinesMap[clickedOrderId] = {};
    }
    updateLinesMap[clickedOrderId] = {
        ...updateLinesMap[clickedOrderId],
        ...getTicketStatusUpdate({
            route,
            clickedLineStatus: clickedLine.status,
            clickedTicketStatus: clickedTicket.status,
            isProductFollowUpEnabled,
        }),
        ...clickedLineUpdates,
        ...clickedParentUpdates,
    };

    for (const line of lines) {
        const { orderId, linePath } = line;
        const { ticket } = yield* getContext(orderId, route);
        if (ticket == null || ticket.status === TicketStatus.Cancelled) {
            return;
        }

        const unfilteredTicket = (yield* getUnfilteredTicket(orderId)) as KdsTicket | undefined;
        if (unfilteredTicket === undefined) {
            return;
        }

        const relatedLine = get(ticket, linePath) as KdsLine;
        const unfilteredRelatedLine = get(unfilteredTicket, linePath) as KdsLine;
        unfilteredRelatedLine.status = clickedLine.status;

        if (updateLinesMap[orderId] == null) {
            updateLinesMap[orderId] = {};
        }

        const updates = getKdsLineUpdates(
            linePath,
            unfilteredRelatedLine,
            route,
            isDoubleValidationEnabled,
            isProductFollowUpEnabled,
            isAssembleEnabled,
        );

        const relatedNextStatus = getLineNextStatus({
            kdsLine: relatedLine,
            isDoubleValidationEnabled,
            isProductFollowUpEnabled,
            isAssembleEnabled,
            isAChildren: false,
            route,
        });

        const relatedParentUpdates = getParentStatusUpdateIfNeeded(
            linePath,
            unfilteredRelatedLine,
            relatedNextStatus,
            unfilteredTicket,
        );

        updateLinesMap[orderId] = {
            ...updateLinesMap[orderId],
            ...getTicketStatusUpdate({
                route,
                clickedLineStatus: relatedLine.status,
                clickedTicketStatus: ticket.status,
                isProductFollowUpEnabled,
            }),
            ...updates,
            ...relatedParentUpdates,
        };
    }
    yield put(updateBatchTickets(clickedRestaurantId, updateLinesMap));
}

function* handleGroupedTicketClicked({
    payload: { orderIds, route },
}: ActionType<typeof groupedTicketClicked>): Generator {
    let groupedRestaurantId = -1;
    const updatedTicketsMap: FireStoreUpdatesMap = {};

    for (const orderId of orderIds) {
        const { ticket, restaurantId, isDoubleValidationEnabled, isAssembleEnabled, isProductFollowUpEnabled } =
            yield* getContext(orderId, route);

        if (updatedTicketsMap[orderId] == null) {
            updatedTicketsMap[orderId] = {};
        }
        if (restaurantId > -1) {
            groupedRestaurantId = restaurantId;
        }
        if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
            return;
        }
        updatedTicketsMap[orderId] = {
            ...updatedTicketsMap[orderId],
            ...getTicketStatusUpdate({ route, clickedTicketStatus: ticket.status, isProductFollowUpEnabled }),
            ...getAllKdsLinesUpdates({
                kdsLines: ticket.kdsLines,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                isAssembleEnabled,
                route,
            }),
        };
    }

    yield put(updateBatchTickets(groupedRestaurantId, updatedTicketsMap));
}

function* handleLineClicked({ payload: { linePath, orderId, route } }: ActionType<typeof lineClicked>): Generator {
    const { ticket, restaurantId, isDoubleValidationEnabled, isAssembleEnabled, isProductFollowUpEnabled } =
        yield* getContext(orderId, route);
    if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
        return;
    }

    const unfilteredTicket = (yield* getUnfilteredTicket(orderId)) as KdsTicket | undefined;
    if (unfilteredTicket === undefined) {
        return;
    }

    const clickedLine = get(ticket, linePath) as KdsLine;
    const unfilteredClickedLine = get(unfilteredTicket, linePath) as KdsLine;
    const kdsLineHasChildren = hasChildren(unfilteredClickedLine);

    let updates: FireStoreUpdates = {};

    if (kdsLineHasChildren) {
        updates = {
            ...updates,
            ...getAllKdsLinesUpdates({
                kdsLines: unfilteredClickedLine.kdsLines,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                parentPath: linePath,
                isAssembleEnabled,
                route,
            }),
        };
    }

    const nextStatus = getLineNextStatus({
        kdsLine: clickedLine,
        isDoubleValidationEnabled,
        isProductFollowUpEnabled,
        isAssembleEnabled,
        isAChildren: false,
        route,
    });

    updates[`${linePath}.status`] = nextStatus;

    const parentUpdates = getParentStatusUpdateIfNeeded(linePath, unfilteredClickedLine, nextStatus, unfilteredTicket);
    updates = { ...updates, ...parentUpdates };

    yield put(
        updateTicket(orderId, restaurantId, {
            ...getTicketStatusUpdate({
                route,
                clickedLineStatus: clickedLine.status,
                clickedTicketStatus: ticket.status,
                isProductFollowUpEnabled,
            }),
            ...updates,
        }),
    );
}

function* handleTicketClicked({ payload: { orderId, route } }: ActionType<typeof ticketClicked>): Generator {
    const { ticket, restaurantId, isDoubleValidationEnabled, isAssembleEnabled, isProductFollowUpEnabled } =
        yield* getContext(orderId, route);
    if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
        return;
    }
    if (ticket.status === TicketStatus.ToDisplay && route === Routes.Home) {
        return;
    }

    yield put(
        updateTicket(orderId, restaurantId, {
            ...getTicketStatusUpdate({ route, clickedTicketStatus: ticket.status, isProductFollowUpEnabled }),
            ...getAllKdsLinesUpdates({
                kdsLines: ticket.kdsLines,
                isDoubleValidationEnabled,
                isProductFollowUpEnabled,
                isAssembleEnabled,
                route,
            }),
        }),
    );
}

function* handleUndisplayGroupedTickets({
    payload: { orderIds },
}: ActionType<typeof undisplayGroupedTickets>): Generator {
    const updateTicketsMap: FireStoreUpdatesMap = {};
    let groupedRestaurantId = -1;

    for (const orderId of orderIds) {
        const { ticket, restaurantId } = yield* getContext(orderId, Routes.Displayer);
        if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
            return;
        }
        if (restaurantId > -1) {
            groupedRestaurantId = restaurantId;
        }
        if (updateTicketsMap[orderId] == null) {
            updateTicketsMap[orderId] = {};
        }
        updateTicketsMap[orderId] = {
            ...updateTicketsMap[orderId],
            ...{ status: TicketStatus.ToDisplay },
        };
    }
    yield put(updateBatchTickets(groupedRestaurantId, updateTicketsMap));
}

function* handleUndisplayTicket({ payload: { orderId } }: ActionType<typeof undisplayTicket>): Generator {
    const { ticket, restaurantId } = yield* getContext(orderId, Routes.Displayer);
    if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
        return;
    }

    yield put(updateTicket(orderId, restaurantId, { status: TicketStatus.ToDisplay }));
}

const getPopOverNextStatus = (type: PopOverType): TicketStatus => {
    switch (type) {
        case TicketStatus.ToCancel:
            return TicketStatus.Cancelled;
        case TicketStatus.Modified:
            return TicketStatus.ToDo;
        default:
            return assertUnreachable(type);
    }
};

function* handlePopOverClicked({ payload: { orderIds, type, route } }: ActionType<typeof popOverClicked>): Generator {
    const updateTicketsMap: FireStoreUpdatesMap = {};
    let groupedRestaurantId = -1;

    for (const orderId of orderIds) {
        const { ticket } = yield* getContext(orderId, route);
        if (ticket == null) {
            return;
        }
        if (groupedRestaurantId < 0) {
            groupedRestaurantId = ticket.restaurantId;
        }
        updateTicketsMap[orderId] = { status: getPopOverNextStatus(type) };
    }

    yield put(updateBatchTickets(groupedRestaurantId, updateTicketsMap));
}

function* handleCleanOldTickets(): Generator {
    const restaurantId = (yield select(getRestaurantId)) as ReturnType<typeof getRestaurantId>;
    if (restaurantId === undefined) {
        throw new Error('RestaurantId is undefined. The configuration must have been fetched');
    }

    const [, recallTiming] = (yield select(getRecallTimeBoundaries)) as ReturnType<typeof getRecallTimeBoundaries>;
    const tickets = (yield select(getBoardTickets)) as ReturnType<typeof getBoardTickets>;
    const updateMap: FireStoreUpdatesMap = {};

    const filteredTickets = tickets.filter(ticketIsPreparingSinceLessThanTiming(recallTiming));

    filteredTickets.forEach((ticket) => {
        updateMap[ticket.orderId] = {
            status: TicketStatus.Done,
        };
    });

    yield put(updateBatchTickets(restaurantId, updateMap));
}

function* handleLineLongPressed({
    payload: { linePath, orderId, route },
}: ActionType<typeof lineLongPressed>): Generator {
    const { ticket, restaurantId, isDoubleValidationEnabled, isAssembleEnabled, isProductFollowUpEnabled } =
        yield* getContext(orderId, route);
    if (!isProductFollowUpEnabled) {
        return;
    }
    if (ticket === undefined || ticket.status === TicketStatus.Cancelled) {
        return;
    }

    const clickedLine = get(ticket, linePath) as KdsLine;

    const previousStatus = getLinePreviousStatus({
        status: clickedLine.status,
        isDoubleValidationEnabled,
        isAssembleEnabled,
    });

    if (previousStatus === clickedLine.status) {
        return;
    }

    let updates: FireStoreUpdates = {
        [`${linePath}.status`]: previousStatus,
    };

    const parentUpdates = getParentStatusUpdateIfNeeded(
        linePath,
        get(ticket, linePath) as KdsLine,
        previousStatus,
        ticket,
    );
    updates = { ...updates, ...parentUpdates };

    const ticketStatusUpdate: FireStoreUpdates =
        ticket.status === TicketStatus.Done ? { status: TicketStatus.ToDo } : {};

    yield put(
        updateTicket(orderId, restaurantId, {
            ...ticketStatusUpdate,
            ...updates,
        }),
    );
}

export function* ticketInteractionSaga(): Generator {
    yield takeEvery(lineClicked, handleLineClicked);
    yield takeEvery(ticketClicked, handleTicketClicked);
    yield takeEvery(groupedLineClicked, handleGroupedLineClicked);
    yield takeEvery(groupedTicketClicked, handleGroupedTicketClicked);
    yield takeEvery(undisplayTicket, handleUndisplayTicket);
    yield takeEvery(undisplayGroupedTickets, handleUndisplayGroupedTickets);
    yield takeEvery(popOverClicked, handlePopOverClicked);
    yield takeEvery(lineLongPressed, handleLineLongPressed);
    yield takeEvery(cleanOldTickets, handleCleanOldTickets);
}
